/**
 * The review that opens: one card per student per class session for the
 * current week, over the last 3 months. Shared items sit on the right of
 * that card and everything else on the left; the review is done when every
 * left column is cleared. motion's layoutId is the same layout animation
 * CollapsibleList uses. Cards enter with the front desk's `AnimateInFlex`.
 */
import { App, Empty, Typography } from "antd"
import { AnimatePresence, LayoutGroup, motion } from "motion/react"
import { useEffect, useMemo, useRef, useState } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import { type ReviewClassImage } from "#components/forms/AddStudent/studentArtworkTilesHelpers"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { type StandoutMomentConstructor } from "#customTypes/composedTypes/standoutTypes"
import { type StoryTimeConstructor } from "#customTypes/composedTypes/storyTimeTypes"
import { knownEventEndsBySessionId } from "#utils/familyDelivery"
import {
  cancelFamilyDelivery,
  scheduleDeliveriesForMoments,
} from "#utils/familyDeliveryWrite"

import ClearedCelebration, { useJustCleared } from "./ClearedCelebration"
import {
  type FamilyDeliveryApi,
  FamilyDeliveryProvider,
} from "./familyDeliveryContext"
import { FamilyRepeatProvider } from "./familyRepeatContext"
import {
  buildStandoutReviewHistory,
  compareHistoryCardsByClassStart,
  groupCardsByDayAndClass,
  type HistoryCard,
  isReviewCleared,
  type ReviewClassGroup,
  type ReviewDayGroup,
  splitReviewCard,
} from "./reviewCardHistory"
import SessionReviewCard from "./SessionReviewCard"
import { type StandoutReviewJoins } from "./standoutReviewHelpers"
import { otherFamilyItemsShared } from "./standoutSessionGroups"
import {
  type FamilyRepeatStudentView,
  useFamilyRepeatJudgments,
} from "./useFamilyRepeatJudgment"

const { Text } = Typography

const CARD_MOVE = {
  layout: { duration: 0.4, ease: "easeInOut" as const },
}

const cardWrapStyle = {
  width: "100%",
  maxWidth: 700,
  minWidth: 0,
}

/** At most 700px wide, then as many as fit. */
const cardGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 700px), 700px))",
  gap: 12,
  alignItems: "start",
  justifyContent: "start",
}

const StandoutReviewColumns = ({
  moments,
  sentMoments = moments,
  scans,
  sessionMoments,
  sessionScans,
  sessionStoryTimes,
  joins,
  notifyAtByKey,
  hideFinished = true,
  hideFinishedClasses = false,
}: {
  moments: readonly StandoutMomentConstructor[]
  /**
   * Unfiltered notes for the last 3 months. The side list and the
   * family-repeat judgment read these. This week's sections use `moments`.
   */
  sentMoments?: readonly StandoutMomentConstructor[]
  scans: readonly ReviewClassImage[]
  /** Unfiltered session contents. The cards stay filtered; the send clock does not. */
  sessionMoments: readonly StandoutMomentConstructor[]
  sessionScans: readonly ReviewClassImage[]
  sessionStoryTimes: readonly StoryTimeConstructor[]
  joins: StandoutReviewJoins
  notifyAtByKey: Record<string, string>
  /** Drop cards whose every item is already shared, hidden, or don't-share. */
  hideFinished?: boolean
  /** Omit a class section once every card in it is decided. */
  hideFinishedClasses?: boolean
}) => {
  const { message } = App.useApp()
  const columns = useMemo(
    () =>
      buildStandoutReviewHistory({
        moments,
        scans,
        // Unfiltered on purpose. A recording joins a card that filtered
        // notes or scans already opened. It does not add a kid.
        storyTimes: sessionStoryTimes,
        now: Date.now(),
        classesById: joins.classesById,
      }),
    [joins.classesById, moments, scans, sessionStoryTimes]
  )
  const ordered = useMemo(() => {
    const byClassStart = (a: HistoryCard, b: HistoryCard) =>
      compareHistoryCardsByClassStart(a, b, joins.classesById)
    return [...columns.cards].sort(byClassStart)
  }, [columns.cards, joins.classesById])
  const repeatByStudent = useFamilyRepeatJudgments(
    sentMoments,
    joins.studentsById
  )
  const deliveryApi = useMemo<FamilyDeliveryApi>(
    () => ({
      reset: (studentId, classSessionId) => {
        if (!classSessionId) return
        void scheduleDeliveriesForMoments(
          [{ studentId, classSessionId }],
          knownEventEndsBySessionId(joins.sessionsByClassId)
        ).catch((err) => {
          console.error("[standout] schedule family delivery failed", err)
          void message.error(
            "Saved the change, but the send timer did not start. Try again."
          )
        })
      },
      unshared: (studentId, classSessionId, excluding) => {
        if (!classSessionId) return
        if (
          otherFamilyItemsShared(
            {
              moments: sessionMoments,
              storyTimes: sessionStoryTimes,
              classImages: sessionScans,
            },
            studentId,
            classSessionId,
            excluding
          )
        ) {
          return
        }
        void cancelFamilyDelivery(studentId, classSessionId).catch((err) => {
          console.error("[standout] cancel family delivery failed", err)
        })
      },
      notifyAt: (studentId, classSessionId) => {
        if (!classSessionId) return null
        return notifyAtByKey[`${studentId}__${classSessionId}`] ?? null
      },
    }),
    [
      joins.sessionsByClassId,
      message,
      notifyAtByKey,
      sessionMoments,
      sessionScans,
      sessionStoryTimes,
    ]
  )

  const grouped = useMemo(
    () => groupCardsByDayAndClass(ordered, joins.classesById, Date.now()),
    [joins.classesById, ordered]
  )
  const reviewCleared = useMemo(() => isReviewCleared(ordered), [ordered])
  const justCleared = useJustCleared(reviewCleared)

  const empty =
    ordered.length === 0 ? (
      <Empty description="Nothing from the last 3 months." />
    ) : null

  return (
    <FamilyDeliveryProvider value={deliveryApi}>
      <FamilyRepeatProvider value={repeatByStudent}>
        <div
          data-testid="sr-review-columns"
          style={{ display: "flex", flexDirection: "column", gap: 12 }}
        >
          <Text type="secondary">Last 3 months. No older cards.</Text>
          {empty}
          <AnimatePresence initial={false}>
            {reviewCleared ? (
              <ClearedCelebration
                key="review-cleared"
                size="review"
                burst={justCleared}
                title="Nothing left to share"
                detail="Every item on this review is shared or marked don't share."
              />
            ) : null}
          </AnimatePresence>
          <LayoutGroup>
            <DaySection
              testId="sr-review-recent"
              title="This week"
              days={grouped.thisWeek}
              joins={joins}
              repeatByStudent={repeatByStudent}
              hideFinished={hideFinished}
              hideFinishedClasses={hideFinishedClasses}
            />
            {grouped.earlier.length > 0 ? (
              <DaySection
                testId="sr-review-earlier"
                title="Earlier"
                days={grouped.earlier}
                joins={joins}
                repeatByStudent={repeatByStudent}
                hideFinished={hideFinished}
                hideFinishedClasses={hideFinishedClasses}
              />
            ) : null}
          </LayoutGroup>
        </div>
      </FamilyRepeatProvider>
    </FamilyDeliveryProvider>
  )
}

const classHeading = (
  group: ReviewClassGroup,
  joins: StandoutReviewJoins
): string => {
  const classObj = group.classId ? joins.classesById[group.classId] : undefined
  const teacherId = classObj?.defaultTeacherId || classObj?.teacherIds?.[0]
  const teacher = teacherId
    ? (joins.usersById[teacherId]?.name?.trim() ?? "")
    : ""
  return [group.label, teacher].filter((part) => part.length > 0).join(" · ")
}

const ReviewCardList = ({
  cards,
  joins,
  repeatByStudent,
}: {
  cards: readonly HistoryCard[]
  joins: StandoutReviewJoins
  repeatByStudent: ReadonlyMap<string, FamilyRepeatStudentView>
}) => (
  <div style={cardGridStyle}>
    <AnimatePresence>
      {cards.map((card) => (
        <motion.div
          key={card.id}
          layout="position"
          layoutId={card.id}
          transition={CARD_MOVE}
          style={cardWrapStyle}
        >
          <AnimateInFlex animation={ANIMATION_BY_KEY.leftSm} col>
            <SessionReviewCard
              card={card}
              joins={joins}
              repeat={repeatByStudent.get(card.studentId)}
            />
          </AnimateInFlex>
        </motion.div>
      ))}
    </AnimatePresence>
  </div>
)

const classIsCleared = (group: ReviewClassGroup): boolean =>
  group.cards.length > 0 &&
  group.cards.every((card) => splitReviewCard(card).cleared)

const ClassGroup = ({
  group,
  joins,
  repeatByStudent,
  hideFinished,
}: {
  group: ReviewClassGroup
  joins: StandoutReviewJoins
  repeatByStudent: ReadonlyMap<string, FamilyRepeatStudentView>
  hideFinished: boolean
}) => {
  const theme = useThemeContext()
  const cleared = classIsCleared(group)
  const [opened, setOpened] = useState(false)
  const wasCleared = useRef(cleared)
  useEffect(() => {
    if (cleared && !wasCleared.current) setOpened(false)
    wasCleared.current = cleared
  }, [cleared])
  const collapsed = cleared && !opened
  const cards =
    hideFinished && !cleared
      ? group.cards.filter((card) => !splitReviewCard(card).cleared)
      : group.cards
  return (
    <div
      data-testid="sr-review-class"
      data-class-id={group.classId ?? ""}
      data-cleared={cleared ? "true" : "false"}
      data-collapsed={collapsed ? "true" : "false"}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        marginLeft: 12,
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${cleared ? theme.eric.green : "transparent"}`,
        background: cleared
          ? `${theme.eric.green}18`
          : "radial-gradient(farthest-corner at 10px 10px, #E6CDB8 0%, #E6CDB800 65%)",
      }}
    >
      <div
        data-testid="sr-review-class-toggle"
        onClick={() => {
          if (!cleared) return
          setOpened((value) => !value)
        }}
        style={{ cursor: cleared ? "pointer" : "default" }}
      >
        <Text data-testid="sr-review-class-label" style={{ fontWeight: 600 }}>
          {classHeading(group, joins)}
        </Text>
      </div>
      {collapsed || cards.length === 0 ? null : (
        <ReviewCardList
          cards={cards}
          joins={joins}
          repeatByStudent={repeatByStudent}
        />
      )}
    </div>
  )
}

const DaySection = ({
  testId,
  title,
  days,
  joins,
  repeatByStudent,
  hideFinished,
  hideFinishedClasses,
}: {
  testId: string
  title: string
  days: readonly ReviewDayGroup[]
  joins: StandoutReviewJoins
  repeatByStudent: ReadonlyMap<string, FamilyRepeatStudentView>
  hideFinished: boolean
  hideFinishedClasses: boolean
}) => {
  const theme = useThemeContext()
  const visibleDays = days.flatMap((day) => {
    const classes = hideFinishedClasses
      ? day.classes.filter((group) => !classIsCleared(group))
      : day.classes
    return classes.length > 0 ? [{ ...day, classes }] : []
  })
  return (
    <section
      data-testid={testId}
      style={{ display: "flex", flexDirection: "column", gap: 16 }}
    >
      <Text strong style={{ fontSize: 16 }}>
        {title}
      </Text>
      {visibleDays.length === 0 ? <Text type="secondary">None</Text> : null}
      {visibleDays.map((day, dayIndex) => (
        <div
          key={day.key}
          data-testid="sr-review-day"
          data-day={day.key}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            borderTop:
              dayIndex === 0 ? undefined : `1px solid ${theme.gray[4]}`,
            paddingTop: dayIndex === 0 ? 0 : 12,
          }}
        >
          <Text strong data-testid="sr-review-day-label">
            {day.label}
          </Text>
          {day.classes.map((group) => (
            <ClassGroup
              key={`${day.key}:${group.key}`}
              group={group}
              joins={joins}
              repeatByStudent={repeatByStudent}
              hideFinished={hideFinished}
            />
          ))}
        </div>
      ))}
    </section>
  )
}

export default StandoutReviewColumns
